import { ScoutingPhotoExifMetadata, ScoutingPhotoLocation } from '../types';

/**
 * Compresses an image file to a lower resolution and quality Base64 string.
 * This is crucial for localStorage-based apps to prevent hitting quota limits.
 *
 * @param file The input File object
 * @param maxWidth Maximum width of the output image (default 1024px)
 * @param quality JPEG quality 0-1 (default 0.7)
 * @returns Promise resolving to the Base64 string
 */
export const compressImage = (file: File, maxWidth = 1024, quality = 0.7): Promise<string> => {
    return new Promise((resolve, reject) => {
        // If not an image (e.g. PDF), read as normal DataURL
        if (!file.type.startsWith('image/')) {
             const reader = new FileReader();
             reader.onload = (e) => resolve(e.target?.result as string);
             reader.onerror = reject;
             reader.readAsDataURL(file);
             return;
        }

        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (event) => {
            const originalDataUrl = event.target?.result as string;
            const img = new Image();
            img.onload = () => {
                const canvas = document.createElement('canvas');
                let width = img.width;
                let height = img.height;

                // Resize logic
                if (width > maxWidth) {
                    height = Math.round((height * maxWidth) / width);
                    width = maxWidth;
                }

                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                
                if (!ctx) {
                     // Fallback if canvas context fails
                     resolve(img.src);
                     return;
                }

                // Draw and compress
                ctx.drawImage(img, 0, 0, width, height);
                
                // Force convert to JPEG for efficient compression, even if input was PNG
                try {
                    resolve(canvas.toDataURL('image/jpeg', quality));
                } catch (error) {
                    console.warn('[images] canvas toDataURL failed, returning original image', error);
                    resolve(originalDataUrl);
                }
            };
            img.onerror = (err) => {
                // Some iOS captures use HEIC/HEIF which canvas cannot decode; fall back to original data URL.
                console.warn('[images] unsupported image format for compression, returning original', err);
                resolve(originalDataUrl);
            };
            img.src = originalDataUrl;
        };
        reader.onerror = (err) => reject(err);
    });
};

export const stripDataUrlPrefix = (value: string) => {
    const commaIndex = value.indexOf(',');
    return commaIndex >= 0 ? value.slice(commaIndex + 1) : value;
};

export const ensureDataUrlBase64 = (value: string, mime = 'image/jpeg') => {
    if (value.startsWith('data:')) return value;
    return `data:${mime};base64,${value}`;
};

const TYPE_SIZES: Record<number, number> = {
    1: 1, // BYTE
    2: 1, // ASCII
    3: 2, // SHORT
    4: 4, // LONG
    5: 8, // RATIONAL
};

const readAscii = (view: DataView, offset: number, length: number) => {
    let str = '';
    for (let i = 0; i < length; i++) {
        const charCode = view.getUint8(offset + i);
        if (charCode === 0) break;
        str += String.fromCharCode(charCode);
    }
    return str;
};

const readValue = (
    view: DataView,
    type: number,
    count: number,
    valueOffset: number,
    littleEndian: boolean,
    tiffStart: number
) => {
    const valueLength = (TYPE_SIZES[type] || 0) * count;
    const baseOffset = valueLength > 4 ? tiffStart + view.getUint32(valueOffset, littleEndian) : valueOffset;

    const readNumber = (getter: (index: number) => number) => (count === 1 ? getter(0) : Array.from({ length: count }, (_, idx) => getter(idx)));

    switch (type) {
        case 2:
            return readAscii(view, baseOffset, count);
        case 3:
            return readNumber((idx) => view.getUint16(baseOffset + idx * 2, littleEndian));
        case 4:
            return readNumber((idx) => view.getUint32(baseOffset + idx * 4, littleEndian));
        case 5:
            return readNumber((idx) => {
                const numerator = view.getUint32(baseOffset + idx * 8, littleEndian);
                const denominator = view.getUint32(baseOffset + idx * 8 + 4, littleEndian);
                if (denominator === 0) return 0;
                return numerator / denominator;
            });
        case 1:
        default:
            return readNumber((idx) => view.getUint8(baseOffset + idx));
    }
};

const readIFD = (view: DataView, offset: number, littleEndian: boolean, tiffStart: number) => {
    const numEntries = view.getUint16(offset, littleEndian);
    const tags = new Map<number, any>();
    for (let i = 0; i < numEntries; i++) {
        const entryOffset = offset + 2 + i * 12;
        const tag = view.getUint16(entryOffset, littleEndian);
        const type = view.getUint16(entryOffset + 2, littleEndian);
        const count = view.getUint32(entryOffset + 4, littleEndian);
        const valueOffset = entryOffset + 8;
        tags.set(tag, readValue(view, type, count, valueOffset, littleEndian, tiffStart));
    }
    const nextOffset = view.getUint32(offset + 2 + numEntries * 12, littleEndian);
    return { tags, nextOffset };
};

const toDecimalDegrees = (values?: number | number[], ref?: string) => {
    if (!Array.isArray(values) || values.length < 3) return undefined;
    const degrees = values[0] + values[1] / 60 + values[2] / 3600;
    if (Number.isNaN(degrees)) return undefined;
    if (ref === 'S' || ref === 'W') return -degrees;
    return degrees;
};

const normalizeExifTimestamp = (value?: string) => {
    if (!value) return undefined;
    const normalized = value.replace(/^(\d{4}):(\d{2}):(\d{2})/, '$1-$2-$3');
    const date = new Date(normalized);
    return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
};

const parseExifMetadata = (buffer: ArrayBuffer): ScoutingPhotoExifMetadata => {
    const view = new DataView(buffer);
    if (view.byteLength < 4 || view.getUint16(0) !== 0xffd8) return {};

    let offset = 2;
    while (offset + 4 < view.byteLength) {
        if (view.getUint8(offset) !== 0xff) break;
        const marker = view.getUint8(offset + 1);
        const length = view.getUint16(offset + 2);

        if (marker === 0xe1) {
            const exifHeader = readAscii(view, offset + 4, 4);
            if (exifHeader !== 'Exif') break;

            const tiffStart = offset + 10;
            const littleEndian = view.getUint16(tiffStart, false) === 0x4949;
            const firstIFDOffset = view.getUint32(tiffStart + 4, littleEndian);
            const { tags: ifd0 } = readIFD(view, tiffStart + firstIFDOffset, littleEndian, tiffStart);

            let taken_at: string | undefined;
            const exifPointer = ifd0.get(0x8769);
            if (typeof exifPointer === 'number') {
                const { tags: exifIFD } = readIFD(view, tiffStart + exifPointer, littleEndian, tiffStart);
                const dateValue = exifIFD.get(0x9003) || exifIFD.get(0x0132);
                if (typeof dateValue === 'string') {
                    taken_at = normalizeExifTimestamp(dateValue);
                }
            }

            let location: ScoutingPhotoLocation | undefined;
            const gpsPointer = ifd0.get(0x8825);
            if (typeof gpsPointer === 'number') {
                const { tags: gpsIFD } = readIFD(view, tiffStart + gpsPointer, littleEndian, tiffStart);
                const lat = toDecimalDegrees(gpsIFD.get(0x0002), gpsIFD.get(0x0001));
                const lng = toDecimalDegrees(gpsIFD.get(0x0004), gpsIFD.get(0x0003));
                const altitudeRaw = gpsIFD.get(0x0006);
                const altitudeRef = gpsIFD.get(0x0005);
                const altitude =
                    typeof altitudeRaw === 'number'
                        ? (altitudeRef === 1 ? -1 : 1) * altitudeRaw
                        : undefined;

                if (lat !== undefined || lng !== undefined || altitude !== undefined) {
                    location = { lat, lng, alt: altitude };
                }
            }

            return { taken_at, location };
        }

        offset += 2 + length;
    }

    return {};
};

export const extractPhotoMetadata = async (file: File): Promise<ScoutingPhotoExifMetadata> => {
    if (!file.type.startsWith('image/')) return {};
    try {
        const buffer = await file.arrayBuffer();
        return parseExifMetadata(buffer);
    } catch (err) {
        console.warn('[images] failed to extract EXIF metadata', err);
        return {};
    }
};

export const extractPhotoMetadataFromUrl = async (url?: string | null): Promise<ScoutingPhotoExifMetadata> => {
    if (!url) return {};
    try {
        const response = await fetch(url);
        if (!response.ok) return {};
        const contentType = response.headers.get('content-type') || '';
        if (contentType && !contentType.startsWith('image/')) return {};
        const buffer = await response.arrayBuffer();
        return parseExifMetadata(buffer);
    } catch (err) {
        console.warn('[images] failed to extract EXIF metadata from URL', err);
        return {};
    }
};
